import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { Button, Select, Textfield } from '@clientbase/clientbase-library'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Divider, MenuItem, Stack, Typography } from '@mui/material'

import InputController from 'components/InputController'

import { toastFirstError } from 'utils/yupResolverWithToast'

import {
  payWithCard,
  payWithCardInstallments,
  payWithSavedCard
} from '../SaasCheckout.service'
import { CardFormData, cardSchema } from './cardSchema'

type CardPaymentProps = {
  billingUuid: string
  /** Assinatura nova: o cartão fica salvo e cobra as próximas mensalidades. */
  recurring: boolean
  amount: number
  /** Cobrança com cartão parcelado (planos anuais, SC-11592). */
  maxInstallments?: number | null
  maxInstallmentsWithoutFee?: number | null
  /** Upgrade de assinatura própria com cartão salvo. */
  savedCardLast4?: string | null
  onPaid: () => void
}

const digits = (value?: string) => (value || '').replace(/\D/g, '')

const formatMoney = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const CardPayment = ({
  billingUuid,
  recurring,
  amount,
  maxInstallments,
  maxInstallmentsWithoutFee,
  savedCardLast4,
  onPaid
}: CardPaymentProps) => {
  const [loading, setLoading] = useState(false)
  const [installments, setInstallments] = useState(1)
  const installmentOptions = Array.from(
    { length: Math.max(maxInstallments || 1, 1) },
    (_, index) => index + 1
  )
  const withoutFee = maxInstallmentsWithoutFee || 1
  // 1x segue no cartão recorrente (fica salvo e renova sozinho); parcelado, não.
  const parceled = installments > 1
  const { control, handleSubmit } = useForm<CardFormData>({
    mode: 'onChange',
    resolver: yupResolver(cardSchema)
  })

  const submit = async (data: CardFormData) => {
    setLoading(true)
    const id = toast.loading('Processando pagamento…')
    const expiration = digits(data.expiration)
    const card = {
      billing_uuid: billingUuid,
      holder_name: data.holder_name,
      document: digits(data.document),
      card_number: digits(data.card_number),
      expiration_month: expiration.slice(0, 2),
      expiration_year: `20${expiration.slice(2)}`,
      cvv: digits(data.cvv)
    }
    const result = parceled
      ? await payWithCardInstallments({ ...card, installments })
      : await payWithCard({
          ...card,
          payment_type: recurring ? 'credit_card_recurring' : 'credit_card'
        })
    toast.update(id, {
      render: result.message,
      type: result.ok ? 'success' : 'error',
      isLoading: false,
      autoClose: 4000
    })
    setLoading(false)
    if (result.ok) onPaid()
  }

  const paySaved = async () => {
    setLoading(true)
    const id = toast.loading('Cobrando no cartão salvo…')
    const result = await payWithSavedCard(billingUuid)
    toast.update(id, {
      render: result.ok ? 'Pagamento aprovado.' : result.message,
      type: result.ok ? 'success' : 'error',
      isLoading: false,
      autoClose: 4000
    })
    setLoading(false)
    if (result.ok) onPaid()
  }

  return (
    <Stack
      gap={2}
      component="form"
      onSubmit={handleSubmit(submit, toastFirstError)}
    >
      {savedCardLast4 && (
        <>
          <Button type="button" fullWidth onClick={paySaved} disabled={loading}>
            Pagar com o cartão final {savedCardLast4}
          </Button>
          <Divider>
            <Typography variant="smLight" color="#777777">
              ou use outro cartão
            </Typography>
          </Divider>
        </>
      )}

      {installmentOptions.length > 1 && (
        <Select
          label="Parcelas"
          value={installments}
          onChange={(e) => setInstallments(Number(e.target.value))}
          fullWidth
        >
          {installmentOptions.map((count) => (
            <MenuItem key={count} value={count}>
              {count === 1
                ? `1x de ${formatMoney(amount)} à vista`
                : count <= withoutFee
                ? `${count}x de ${formatMoney(amount / count)} sem juros`
                : `${count}x com juros do cartão`}
            </MenuItem>
          ))}
        </Select>
      )}

      {recurring && !parceled && (
        <Typography variant="smLight" color="#777777">
          O cartão fica salvo para as próximas cobranças da assinatura. Você
          pode trocar quando quiser.
        </Typography>
      )}

      {parceled && (
        <Typography variant="smLight" color="#777777">
          No parcelado o cartão não fica salvo: a renovação da assinatura chega
          como fatura, para pagar por PIX ou cartão.
        </Typography>
      )}

      <InputController control={control} name="holder_name">
        <Textfield fullWidth label="Nome impresso no cartão" />
      </InputController>
      <InputController control={control} name="document">
        <Textfield fullWidth label="CPF/CNPJ do titular" mask="cpfCnpj" />
      </InputController>
      <InputController control={control} name="card_number">
        <Textfield
          fullWidth
          label="Número do cartão"
          inputMode="numeric"
          placeholder="0000 0000 0000 0000"
        />
      </InputController>
      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5}>
        <InputController control={control} name="expiration">
          <Textfield
            fullWidth
            label="Validade"
            placeholder="MM/AA"
            inputMode="numeric"
          />
        </InputController>
        <InputController control={control} name="cvv">
          <Textfield fullWidth label="CVV" inputMode="numeric" />
        </InputController>
      </Box>

      <Button fullWidth type="submit" disabled={loading}>
        Pagar com cartão
      </Button>
    </Stack>
  )
}

export default CardPayment
